import type { RevenueAnalysisDimension } from '@/shared/api/modules/facilities';
import { chartColor } from '@/shared/lib/chart-theme';

type SeriesPalette = { color: string; soft: string };

const token = (name: string): SeriesPalette => ({
  color: chartColor(`--color-${name}` as `--color-${string}`),
  soft: chartColor(`--color-${name}-soft` as `--color-${string}`),
});

const PAYER_TYPE: Record<string, () => SeriesPalette> = {
  private: () => token('revenue-private'),
  medicaid: () => token('revenue-medicaid'),
};

/**
 * Levels of care are keyed by the label the backend builds ("Level 3"), not by a
 * number, because that label is what `careLevelLabelSql` emits and what the KPI
 * card shows.
 */
const CARE_LEVELS: Record<string, () => SeriesPalette> = {
  'Level 1': () => token('care-level-1'),
  'Level 2': () => token('care-level-2'),
  'Level 3': () => token('care-level-3'),
  'Level 4': () => token('care-level-4'),
  'Level 5': () => token('care-level-5'),
  'Level 6': () => token('care-level-6'),
  // Revenue the backend could not attribute to a level. Keyed by the sentinel
  // UNASSIGNED_SERIES_KEY, not by its display label.
  unassigned: () => token('care-level-unassigned'),
};

/** Keys are the upper-cased `Invoice.payerType` values the ALIS sync writes. */
const INSURERS: Record<string, () => SeriesPalette> = {
  'BLUE CROSS': () => token('insurer-1'),
  MOLINA: () => token('insurer-2'),
  STARS: () => token('insurer-3'),
  UHC: () => token('insurer-4'),
};

/**
 * For series the design never drew — a seventh care level, or a Medicaid payer
 * outside the four named ones (the data holds at least one). Cycling a known
 * palette beats generating a colour: it stays inside the app's hues and reads as
 * deliberate rather than random.
 */
const FALLBACK = [
  () => token('insurer-3'),
  () => token('care-level-2'),
  () => token('care-level-5'),
  () => token('insurer-1'),
];

const PALETTES: Record<
  RevenueAnalysisDimension,
  Record<string, () => SeriesPalette>
> = {
  payerType: PAYER_TYPE,
  levelsOfCare: CARE_LEVELS,
  medicaidInsurer: INSURERS,
};

/**
 * Colour for a series, resolved at call time so it follows a theme toggle
 * (see `chartColor`). `index` only matters for the fallback path.
 */
export const seriesPalette = (
  dimension: RevenueAnalysisDimension,
  key: string,
  index: number
): SeriesPalette => {
  const known = PALETTES[dimension][key];
  return known ? known() : FALLBACK[index % FALLBACK.length]();
};

/** Solid colour dimmed for the "some other card is hovered" state. */
export const dimmed = (color: string): string => `${color}40`;
